import { EM_DASH } from '../lib/typography';
import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import { UI, playUI } from '../utils/arena/uiSounds';
import { getDraftStandings, getDraftPickHistory } from '../utils/arena/draftApi';
import { resolvePackContents } from '../utils/arena/packsApi';
import DeckCardTile from './DeckCardTile';
import CardGrid from './CardGrid';
import { TabBar } from './ui/TabBar';
import { isFoilFinish, resolvePrinting } from '../utils/sorcery/foil';
import { foilZonesForCard } from '../utils/sorcery/foilZones';
import PackOpeningFX from './PackOpeningFX';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  PANEL_STYLE, BEVELED_BTN, GOLD_BTN, TAB_ACTIVE_TEXT, TAB_INACTIVE, TAB_BAR_STYLE,
  FourCorners, OrnamentalDivider, RADIUS,
} from '../lib/medievalTheme';
import { t } from '../localization/t';
import { isFreePlay } from '../utils/freePlay';
import AmbientParticles from './AmbientParticles';
import { BackButton } from './ui/navButtons';

const RARITY_COLORS = {
  Unique: '#e8c840',
  Elite: '#c860e0',
  Exceptional: '#4898e0',
  Ordinary: TEXT_BODY,
};

function ordinal(n) {
  if (n === 1) return t('1st');
  if (n === 2) return t('2nd');
  if (n === 3) return t('3rd');
  return t('{n}th', { n });
}

export default class DraftResults extends Component {
  constructor(props) {
    super(props);
    // Only the draft winner gets the celebratory card-reveal animation.
    // The prize card itself is on props.prizes.prizeCard for every player
    // (see server comment on draft:complete), so the banner renders for
    // everyone but the reveal is gated to whoever actually won.
    // Free play awards no prizes.
    const hasPrize = !!props.prizes?.prizeCard && !isFreePlay();
    const winnerId = props.finalStandings?.[0]?.playerId;
    const isWinner = !!winnerId && props.profile?.id === winnerId;
    this.state = {
      tab: 'standings',
      standings: props.finalStandings || [],
      picks: [],
      picksLoading: false,
      showPrizeReveal: hasPrize && isWinner,
      prizeRevealed: false,
    };
  }

  componentDidMount() {
    if (!this.state.standings.length) this.loadStandings();
    // Auto-trigger prize reveal animation after a short delay
    if (this.state.showPrizeReveal) {
      setTimeout(() => {
        this.setState({ prizeRevealed: true });
        playUI(UI.PACK_OPENING, { volume: 0.6 });
        playUI(UI.ACHIEVEMENT);
      }, 800);
    }
  }

  loadStandings = async () => {
    try {
      const data = await getDraftStandings(this.props.eventId);
      this.setState({ standings: data.standings || [] });
    } catch (err) {
      console.error('[DraftResults] loadStandings failed:', err);
    }
  };

  loadPickHistory = async () => {
    if (this.state.picks.length) return;
    this.setState({ picksLoading: true });
    try {
      const data = await getDraftPickHistory(this.props.eventId);
      this.setState({ picks: data.picks || [], picksLoading: false });
    } catch (err) {
      console.error('[DraftResults] loadPickHistory failed:', err);
      this.setState({ picksLoading: false });
    }
  };

  handleTabChange = (tab) => {
    this.setState({ tab });
    if (tab === 'picks') this.loadPickHistory();
  };

  // CardGrid callbacks for the drafted-cards grid. Foil entries route their art
  // through the grid's one shared WebGL scene (imageHidden tile); non-foil keep
  // their DOM <img>.
  getDraftedItemKey = (entry, i) => `card-${i}-${entry.card.unique_id}`;

  getDraftedItemFoil = (entry) => {
    const printing = entry.printing || {};
    if (!isFoilFinish(printing.foiling)) return null;
    return { imageUrl: printing.image_url || '', zones: foilZonesForCard(entry.card) };
  };

  renderDraftedTile = (entry, { index, imageHidden, onTilt }) => (
    <div style={{ width: '100%', aspectRatio: '63/88' }}>
      <DeckCardTile
        entry={{ card: entry.card, printing: entry.printing || {}, zone: 'spellbook', entryIndex: index }}
        isSelected={false}
        imageHidden={imageHidden}
        onTilt={onTilt}
        onClick={() => {}}
        onHoverChange={() => {}}
      />
    </div>
  );

  render() {
    const { profile, onBack, draftedCards, prizes, sorceryCards, mode } = this.props;
    const { tab, standings, picks, picksLoading } = this.state;

    const isSealed = mode === 'sealed';
    const modeLabel = isSealed ? t('Sealed') : t('Draft');
    const cardsTabLabel = isSealed ? t('Sealed Pool') : t('Drafted Cards');
    // Sealed has no pick history. Every player got the full pool on open.
    const resultTabs = [
      { key: 'standings', label: t('Standings') },
      { key: 'cards', label: cardsTabLabel },
      ...(isSealed ? [] : [{ key: 'picks', label: t('Pick History') }]),
    ];

    const myRank = standings.findIndex((s) => s.playerId === profile?.id) + 1;
    const winner = standings[0];
    const isWinner = !!winner && profile?.id === winner.playerId;

    // Resolve the prize card against the sorcery DB so the banner can
    // show the real tile (foil included). resolvePackContents handles
    // the { cardId, printingId, foiling, rarity } shape the server sends.
    // Free play awards no prizes and adds nothing to the collection.
    const freePlay = isFreePlay();
    const prizeResolved = prizes?.prizeCard && !freePlay
      ? resolvePackContents([prizes.prizeCard], sorceryCards || [])[0]
      : null;

    return (
      <div className="fixed inset-0 z-50 flex flex-col overflow-hidden select-none" style={{ background: '#08080a' }}>
        <div className="absolute inset-0" style={{ background: `url('/hub-bg.webp') center/cover no-repeat`, filter: 'blur(6px)', transform: 'scale(1.02)' }} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.8) 100%)' }} />
        <AmbientParticles />

        <div className="relative z-10 flex-1 flex flex-col items-center overflow-hidden p-6">
          <div className="w-full max-w-[48cqw]">
            {/* Header */}
            <div className="text-center mb-2">
              <h2 className="text-2xl font-bold arena-heading" style={{ color: ACCENT_GOLD, textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 0 20px rgba(200,160,60,0.15)' }}>
                {t('{mode} Complete', { mode: modeLabel })}
              </h2>
              {myRank > 0 ? (
                <div className="text-sm mt-1" style={{ color: myRank === 1 ? ACCENT_GOLD : TEXT_BODY }}>
                  {prizes?.coinsEarned && !freePlay
                    ? t('You finished {place}. Earned {n} coins', { place: ordinal(myRank), n: prizes.coinsEarned })
                    : t('You finished {place}', { place: ordinal(myRank) })}
                </div>
              ) : null}
            </div>
            <OrnamentalDivider className="mb-4" />

            {/* Tabs */}
            <TabBar className="flex mb-4" style={TAB_BAR_STYLE} activeIndex={resultTabs.findIndex((item) => item.key === tab)}>
              {resultTabs.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  data-tab-item
                  className="px-4 py-2 text-xs font-semibold cursor-pointer"
                  style={tab === item.key ? TAB_ACTIVE_TEXT : TAB_INACTIVE}
                  data-sound={UI.TAB}
                  onClick={() => this.handleTabChange(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </TabBar>

            {/* Prize banner. Persistent across tabs so everyone can
                see what the winner claimed. Framed as neutral info
                ("Won by X") for non-winners to avoid feeling like
                salt in the wound, and as "Won by you 🏆" for the
                winner as a quiet summary after their reveal. */}
            {prizeResolved ? (
              <div
                className="flex items-center gap-3 p-3 mb-4 rounded"
                style={{
                  ...PANEL_STYLE,
                  borderColor: isWinner ? `${GOLD} 0.35)` : `${GOLD} 0.12)`,
                }}
              >
                <div className="shrink-0" style={{ width: '3.75cqw', aspectRatio: '63/88' }}>
                  <DeckCardTile
                    entry={{ card: prizeResolved.card, printing: prizeResolved.printing || {}, zone: 'spellbook', entryIndex: 0 }}
                    isSelected={false}
                    onClick={() => {}}
                    onHoverChange={() => {}}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[0.625cqw] font-semibold" style={{ color: TEXT_MUTED }}>{t('{mode} Foil Prize', { mode: modeLabel })}</div>
                  <div className="text-sm font-bold truncate" style={{ color: RARITY_COLORS[prizes.prizeCard.rarity] || TEXT_PRIMARY }}>
                    {prizeResolved.card.name}
                    <span className="ml-2 text-xs font-normal" style={{ color: TEXT_MUTED }}>
                      {t('{rarity} Foil', { rarity: prizes.prizeCard.rarity })}
                    </span>
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: isWinner ? ACCENT_GOLD : TEXT_BODY }}>
                    {isWinner
                      ? t('🏆 Won by you')
                      : winner ? t('Won by {name}', { name: winner.playerName }) : isSealed ? t('Awarded to the sealed champion') : t('Awarded to the draft champion')}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100cqh - 280px)' }}>
              {/* Standings */}
              {tab === 'standings' ? (
                <div className="rounded overflow-hidden" style={PANEL_STYLE}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${GOLD} 0.15)` }}>
                        <th className="text-left px-4 py-2 text-[0.625cqw] font-semibold" style={{ color: TEXT_MUTED }}>#</th>
                        <th className="text-left px-4 py-2 text-[0.625cqw] font-semibold" style={{ color: TEXT_MUTED }}>{t('Player')}</th>
                        <th className="text-center px-4 py-2 text-[0.625cqw] font-semibold" style={{ color: TEXT_MUTED }}>W</th>
                        <th className="text-center px-4 py-2 text-[0.625cqw] font-semibold" style={{ color: TEXT_MUTED }}>L</th>
                        {freePlay ? null : <th className="text-right px-4 py-2 text-[0.625cqw] font-semibold" style={{ color: TEXT_MUTED }}>{t('Prize')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {standings.map((entry, i) => {
                        const isMe = entry.playerId === profile?.id;
                        const place = i + 1;
                        return (
                          <tr
                            key={entry.playerId}
                            style={{
                              background: isMe ? 'rgba(212,168,67,0.06)' : 'transparent',
                              borderBottom: `1px solid ${GOLD} 0.06)`,
                            }}
                          >
                            <td className="px-4 py-2.5 tabular-nums font-bold" style={{ color: place <= 3 ? ACCENT_GOLD : TEXT_MUTED }}>
                              {place === 1 ? '🏆' : place}
                            </td>
                            <td className="px-4 py-2.5 font-semibold" style={{ color: isMe ? ACCENT_GOLD : TEXT_PRIMARY }}>{entry.playerName}</td>
                            <td className="px-4 py-2.5 text-center tabular-nums" style={{ color: '#6dba6d' }}>{entry.wins}</td>
                            <td className="px-4 py-2.5 text-center tabular-nums" style={{ color: '#c45050' }}>{entry.losses}</td>
                            {freePlay ? null : (
                              <td className="px-4 py-2.5 text-right text-xs" style={{ color: entry.prizeCoins ? ACCENT_GOLD : TEXT_MUTED }}>
                                {entry.prizeCoins ? t('+{n} coins', { n: entry.prizeCoins }) : EM_DASH}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}

              {/* Drafted cards. Resolve the server-sent `{ cardId,
                  printingId, cardName, rarity }` entries against the
                  sorcery card database so each tile has the real card
                  object + printing DeckCardTile expects. */}
              {tab === 'cards' ? (
                <div className="flex flex-col" style={{ height: 'calc(100cqh - 300px)' }}>
                  <div className="text-xs mb-2 shrink-0" style={{ color: '#6dba6d' }}>
                    {freePlay
                      ? t('{n} cards in your pool', { n: draftedCards?.length || 0 })
                      : t('{n} cards added to your collection', { n: draftedCards?.length || 0 })}
                  </div>
                  <CardGrid
                    items={resolvePackContents(draftedCards || [], this.props.sorceryCards || [])}
                    gridSize={6}
                    getItemKey={this.getDraftedItemKey}
                    getItemFoil={this.getDraftedItemFoil}
                    renderTile={this.renderDraftedTile}
                  />
                </div>
              ) : null}

              {/* Pick history. Grouped by pack, rendered with the same
                  card grid component the rest of the app uses. Each tile
                  carries the pick number as a small badge so draft
                  analysis still has ordering context. */}
              {tab === 'picks' ? (
                picksLoading ? (
                  <div className="text-center py-8 text-sm" style={{ color: TEXT_MUTED }}>{t('Loading pick history...')}</div>
                ) : (
                  <div className="space-y-4">
                    {[1, 2, 3].map((packNum) => {
                      const packPicks = picks.filter((p) => p.packNumber === packNum);
                      if (!packPicks.length) return null;
                      const resolved = resolvePackContents(packPicks, this.props.sorceryCards || []);
                      return (
                        <div key={packNum}>
                          <div className="text-xs font-semibold mb-2" style={{ color: TEXT_MUTED }}>{t('Pack {n}', { n: packNum })}</div>
                          <div className="grid gap-1.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(7.5cqw, 1fr))' }}>
                            {resolved.map((entry, i) => {
                              const pick = packPicks[i];
                              return (
                                <div
                                  key={`pick-${packNum}-${i}-${entry.card.unique_id}`}
                                  className="relative"
                                  style={{ width: '100%', aspectRatio: '63/88' }}
                                >
                                  <DeckCardTile
                                    entry={{ card: entry.card, printing: entry.printing || {}, zone: 'spellbook', entryIndex: i }}
                                    isSelected={false}
                                    onClick={() => {}}
                                    onHoverChange={() => {}}
                                  />
                                  <div
                                    className="absolute top-1 left-1 text-[0.625cqw] tabular-nums font-bold px-1.5 py-0.5 rounded pointer-events-none"
                                    style={{ background: 'rgba(0,0,0,0.75)', color: ACCENT_GOLD, border: `1px solid ${GOLD} 0.3)` }}
                                  >
                                    {pick.pickNumber}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              ) : null}
            </div>

            {/* Back button */}
            <div className="mt-4 flex justify-center">
              <BackButton title={t('Return to Hub')} onClick={onBack} />
            </div>
          </div>
        </div>

        {/* Prize card reveal overlay */}
        <AnimatePresence>
          {this.state.showPrizeReveal ? this.renderPrizeReveal() : null}
        </AnimatePresence>
      </div>
    );
  }

  renderPrizeReveal() {
    const { prizes, sorceryCards, mode } = this.props;
    const prizeCard = prizes?.prizeCard;
    if (!prizeCard) return null;
    const championLabel = mode === 'sealed' ? t('Sealed Champion') : t('Draft Champion');

    const card = (sorceryCards || []).find((c) => c.unique_id === prizeCard.cardId);
    if (!card) return null;
    const printing = resolvePrinting(card, prizeCard.printingId, 'F');
    const rarity = prizeCard.rarity || card.rarity || 'Ordinary';

    const RARITY_GLOW = {
      Ordinary: 'rgba(255,255,255,0.15)',
      Exceptional: 'rgba(59,130,246,0.5)',
      Elite: 'rgba(168,85,247,0.6)',
      Unique: 'rgba(245,158,11,0.7)',
      Avatar: 'rgba(220,40,40,0.6)',
    };

    return (
      <motion.div
        key="prize-reveal"
        className="fixed inset-0 z-[200] flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.85)' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
      >
        <PackOpeningFX active={this.state.prizeRevealed} />

        <div className="relative flex flex-col items-center z-10">
          {/* Radial glow behind the card */}
          <motion.div
            className="absolute"
            style={{
              width: '25cqw', height: '25cqw', borderRadius: '50%',
              background: `radial-gradient(circle, ${RARITY_GLOW[rarity]} 0%, transparent 70%)`,
              filter: 'blur(40px)',
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={this.state.prizeRevealed ? { scale: 1.5, opacity: 1 } : {}}
            transition={{ duration: 1.2, ease: 'easeOut' }}
          />

          {/* Title */}
          <motion.div
            className="text-center mb-6 relative z-10"
            initial={{ opacity: 0, y: -20 }}
            animate={this.state.prizeRevealed ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <div className="text-xs mb-1" style={{ color: ACCENT_GOLD }}>{t('1st Place')}</div>
            <div className="text-2xl font-bold arena-heading" style={{ color: TEXT_PRIMARY, textShadow: '0 2px 8px rgba(0,0,0,0.5), 0 0 30px rgba(212,168,67,0.3)' }}>
              {championLabel}
            </div>
          </motion.div>

          {/* Card. aspectRatio keeps the frame in 63:88 at every canvas
              scale. Using a raw-pixel height alongside a cqw width
              produced a too-tall frame on smaller canvases, hence the
              stroke extending below the card art. The visual frame
              (border + glow) is mounted as an absolute-positioned
              underlay so the DeckCardTile hover-pivot can rotate past
              the frame bounds without an overflow:hidden clip. */}
          <motion.div
            className="relative z-10"
            style={{ width: '13.75cqw', aspectRatio: '63/88', perspective: 1200 }}
            initial={{ opacity: 0, scale: 0.3, rotateY: 180 }}
            animate={this.state.prizeRevealed ? { opacity: 1, scale: 1, rotateY: 0 } : {}}
            transition={{ delay: 0.4, type: 'spring', stiffness: 200, damping: 20 }}
          >
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: RADIUS.card,
                boxShadow: `0 0 40px ${RARITY_GLOW[rarity]}, 0 20px 60px rgba(0,0,0,0.6)`,
                border: `2px solid ${RARITY_GLOW[rarity]}`,
                pointerEvents: 'none',
              }}
            />
            <DeckCardTile
              entry={{ card, printing: printing || {}, zone: 'spellbook', entryIndex: 0 }}
              isSelected={false}
              onClick={() => {}}
              onHoverChange={() => {}}
            />
          </motion.div>

          {/* Card name + rarity */}
          <motion.div
            className="text-center mt-4 relative z-10"
            initial={{ opacity: 0, y: 10 }}
            animate={this.state.prizeRevealed ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.8, duration: 0.4 }}
          >
            <div className="text-lg font-bold" style={{ color: RARITY_COLORS[rarity] || TEXT_PRIMARY }}>{card.name}</div>
            <div className="text-xs" style={{ color: TEXT_MUTED }}>{t('{rarity} Foil. Added to your collection', { rarity })}</div>
          </motion.div>

          {/* Continue button */}
          <motion.div
            className="mt-6 relative z-10"
            initial={{ opacity: 0 }}
            animate={this.state.prizeRevealed ? { opacity: 1 } : {}}
            transition={{ delay: 1.5 }}
          >
            <button
              type="button"
              className="px-8 py-2.5 text-sm font-semibold cursor-pointer transition-all"
              style={{ ...GOLD_BTN, borderRadius: 'var(--radius-lg)' }}
              data-sound={UI.CONFIRM}
              onClick={() => this.setState({ showPrizeReveal: false })}
            >
              {t('Continue')}
            </button>
          </motion.div>
        </div>
      </motion.div>
    );
  }
}
